import {
  Button,
  ButtonText,
  Flex,
  Icon,
  OrganisationHeaderWithImage,
  Text,
} from '@percent/lemonade'
import styled from 'styled-components'
import { useEffect, useRef, useState } from 'react'
import { OrganisationCardProps } from './organisation-card.types'
export type { OrganisationCardProps } from './organisation-card.types'

export const AB488_SUPPORT_URL =
  'https://help.goodstack.org/understanding-california-ab488-rules'

const StyledCardContainer = styled.div`
  ${({ theme }) => `
  padding: ${theme.sizes.m};
  border-radius: 8px;
  box-shadow: 0 2px 16px 0 #0D0D0D14;

  @media (max-width: 767px) {
    border: 1px solid var(--colors-gray-90);
    box-shadow: none;
  }
  `}
`

const StyledCardContentDescriptionContainer = styled.div`
  margin-bottom: 24px;
`

const StyledCardContentDescription = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: #494550;
  margin-bottom: 0;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &.showMore {
    -webkit-line-clamp: unset;
  }
`

const StyledCardButton = styled.div`
  display: flex;
  align-items: center;

  & > button {
    ${({ theme }) => `
      margin-right: ${theme.sizes.s};
    `}
  }
`

const StyledCardButtonInfo = styled.div`
  display: flex;
  align-items: center;
  margin: 0.5rem -0.25rem 0;
`

const StyledCardActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  ${StyledCardButtonInfo} {
    margin: 0;
  }
`

const StyledLink = styled.a`
  ${({ theme }) => `
    color: ${theme.colors.primary400};
    text-decoration: underline;
    text-underline-offset: 0.25rem;
  `}
`

export const OrganisationCard = ({
  id,
  registryId,
  iconUrl,
  title,
  description,
  countryCode,
  website,
  cardFooterArea,
  buttonText,
  moreText,
  lessText,
  onDonateClicked,
  cardButtonInfo,
  additionalActions,
  donationOptions,
  badge,
  badgeTooltip,
}: OrganisationCardProps) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null)
  const [hasMore, setHasMore] = useState(false)
  const [showMore, setShowMore] = useState(false)

  useEffect(() => {
    if (descriptionRef.current) {
      setHasMore(
        descriptionRef.current.scrollHeight >
          descriptionRef.current.clientHeight
      )
    }
  }, [])

  return (
    <StyledCardContainer role="listitem">
      <div>
        <OrganisationHeaderWithImage
          title={title}
          countryCode={countryCode}
          registryId={registryId}
          iconUrl={iconUrl}
          website={website}
          badge={badge}
          badgeTooltip={badgeTooltip}
        />
        {description ? (
          <StyledCardContentDescriptionContainer>
            <StyledCardContentDescription
              ref={descriptionRef}
              className={showMore ? 'showMore' : ''}
            >
              {description}
            </StyledCardContentDescription>
            {hasMore && (
              <ButtonText onClick={() => setShowMore((prev) => !prev)}>
                {showMore ? lessText : moreText}
              </ButtonText>
            )}
          </StyledCardContentDescriptionContainer>
        ) : null}

        {donationOptions.kind === 'disallowed' && (
          <StyledCardButton>{additionalActions}</StyledCardButton>
        )}
        {donationOptions.kind === 'disallowed' ? (
          <Flex align="center" gap="0.25rem" mt="0.5rem">
            <div style={{ flexShrink: 0, display: 'flex' }}>
              <Icon name="info" size="m" color="neutral300" />
            </div>
            <Text size="small">
              {donationOptions.reason}{' '}
              {donationOptions.link && (
                <StyledLink
                  href={donationOptions.link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {donationOptions.link.text}
                </StyledLink>
              )}
            </Text>
          </Flex>
        ) : (
          <StyledCardActionsRow>
            <StyledCardButton>
              {donationOptions.kind === 'allowed' && (
                <Button
                  onClick={() => onDonateClicked(id)}
                  size="small"
                  shape="oval"
                >
                  {buttonText}
                </Button>
              )}
              {additionalActions}
            </StyledCardButton>
            <StyledCardButtonInfo>{cardButtonInfo}</StyledCardButtonInfo>
          </StyledCardActionsRow>
        )}

        {donationOptions.kind === 'disallowed' && (
          <StyledCardButtonInfo>{cardButtonInfo}</StyledCardButtonInfo>
        )}
        {cardFooterArea}
      </div>
    </StyledCardContainer>
  )
}
